import { useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Skeleton,
  Pagination,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { useGetSectionStyles } from "../../../hooks/panelEditor/useGetSectionStyles";
import { usePutSectionStyle } from "../../../hooks/panelEditor/usePutSectionStyle";
import { useSectionTitle } from "../../../hooks/panelEditor/useGetSectionTitle";
import ColorPickerModal from "./ColorPickerModal.tsx";
import SectionEditModal from "./SectionEditModal";
import SectionStyleCard from "./SectionStyleCard";
import { toastAlert } from "../../../utils/AlertContext";

const SectionsStylesEditor = ({ token, idDocument }) => {
  const {
    stylesBySectionId,
    setStylesBySectionId,
    loading,
    error: getError,
    updateLocalSectionStyle,
    refresh: refreshSectionStyles
  } = useGetSectionStyles(token, idDocument);

  const {
    saving,
    saveError,
    saveSectionStyle,
  } = usePutSectionStyle(token, setStylesBySectionId);

  const { sectionTitle, refresh: refreshSectionTitle } = useSectionTitle(token, idDocument);

  const [colorModal, setColorModal] = useState({
    open: false,
    current: "#000000",
    onApply: null,
    title: "Seleccionar color",
  });
  const [expandedSectionId, setExpandedSectionId] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const [editModal, setEditModal] = useState({ open: false, section: null });

  const [page, setPage] = useState(1);
  const itemsPerPage = 6;

  const paginatedSections = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return sectionTitle.slice(startIndex, startIndex + itemsPerPage);
  }, [sectionTitle, page]);

  const openColorModal = useCallback((currentColor, onApply, title) =>
    setColorModal({
      open: true,
      current: currentColor || "#000000",
      onApply,
      title: title || "Seleccionar color",
    }),
  []);

  const closeColorModal = useCallback(
    () => setColorModal((p) => ({ ...p, open: false })),
    []
  );

  const toggleSection = useCallback(
    (sectionId) =>
      setExpandedSectionId((prev) => (prev === sectionId ? null : sectionId)),
    []
  );

  const handleLocalChange = useCallback(
    (sectionId, attributeName, updates) => {
      updateLocalSectionStyle(sectionId, attributeName, updates);
      setPendingChanges((prev) => ({
        ...prev,
        [sectionId]: {
          ...prev[sectionId],
          [attributeName]: {
            ...prev[sectionId]?.[attributeName],
            ...updates,
          },
        },
      }));
    },
    [updateLocalSectionStyle]
  );

  const saveAllChanges = async () => {
    const promises = [];
    for (const [sectionId, attributes] of Object.entries(pendingChanges)) {
      if (Object.keys(attributes).length > 0) {
        promises.push(saveSectionStyle(Number(sectionId), attributes));
      }
    }
    
    try {
      const results = await Promise.all(promises);
      const failed = results.filter(r => r && r.success === false);
      
      if (failed.length > 0) {
        throw new Error(failed[0].error || "Ocurrió un error guardando");
      }
      
      toastAlert("¡Cambios guardados exitosamente!", "success");
      setPendingChanges({});
    } catch (err) {
      console.error("Error saving changes:", err);
      toastAlert(`No se pudieron guardar los cambios: ${err.message}`, "error");
    }
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;
  const error = getError || saveError;

  const handleEditSection = useCallback(
    (section) => setEditModal({ open: true, section }),
    []
  );
  const handleCloseEditModal = useCallback(
    () => setEditModal({ open: false, section: null }),
    []
  );
  const handleSectionTypeUpdated = useCallback(async () => {
    await refreshSectionTitle();
    await refreshSectionStyles();
  }, [refreshSectionTitle, refreshSectionStyles]);

  if (loading && sectionTitle.length === 0) {
    return (
      <Stack spacing={3}>
        {[1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={80}
            sx={{ borderRadius: "16px" }}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Box position="relative">
      <Stack spacing={1}>
        {paginatedSections.map((s) => (
          <SectionStyleCard
            key={s.id}
            section={s}
            sectionStyles={stylesBySectionId[s.id]}
            pending={pendingChanges[s.id]}
            expanded={expandedSectionId === s.id}
            onToggle={toggleSection}
            onEdit={handleEditSection}
            onLocalChange={handleLocalChange}
            onOpenColorPicker={openColorModal}
          />
        ))}

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography>Cargando estilos de secciones...</Typography>
          </Box>
        )}

        {error && (
          <Box display="flex" justifyContent="center" py={2}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
      </Stack>

      {sectionTitle.length > itemsPerPage && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={Math.ceil(sectionTitle.length / itemsPerPage)}
            page={page}
            onChange={(e, p) => setPage(p)}
            color="primary"
            size="large"
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      )}

      {hasPendingChanges && (
        <Box sx={{ mt: 4, p: 3, bgcolor: "#f5f5f5", borderRadius: "12px", position: "relative" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Cambios Pendientes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tienes cambios sin guardar en {Object.keys(pendingChanges).length} sección(es)
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={saveAllChanges}
              disabled={saving}
              sx={{
                borderRadius: "50px",
                textTransform: "capitalize",
                px: 4,
                py: 1.5,
                bgcolor: "#1976d2",
                "&:hover": { bgcolor: "#1565c0" },
                "&:disabled": { bgcolor: "#e0e0e0", color: "#9e9e9e" },
              }}
            >
              {saving ? "Guardando..." : "Guardar Todos los Cambios"}
            </Button>
          </Stack>
        </Box>
      )}

      <ColorPickerModal
        open={colorModal.open}
        title={colorModal.title}
        color={colorModal.current}
        onApply={colorModal.onApply}
        onClose={closeColorModal}
      />

      {editModal.open && (
        <SectionEditModal
          isOpen={editModal.open}
          onClose={handleCloseEditModal}
          onSave={handleSectionTypeUpdated}
          section={editModal.section}
          token={token}
        />
      )}
    </Box>
  );
};

export default SectionsStylesEditor;
